import { useState, useEffect } from 'react'
import { Card } from '../../components/common/UI'
import { AdminLayout } from '../../components/layouts/Layout'
import { Users, Building, Megaphone, DollarSign, TrendingUp, AlertCircle } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { userService, merchantService, businessService, advertisementService, creditPurchaseService } from '../../services/api'
import { useAdminAuth } from '../../hooks/useAdminAuth'

export default function Dashboard() {
  const { admin } = useAdminAuth()
  const [users, setUsers] = useState<any[]>([])
  const [merchants, setMerchants] = useState<any[]>([])
  const [businesses, setBusinesses] = useState<any[]>([])
  const [advertisements, setAdvertisements] = useState<any[]>([])
  const [creditPurchases, setCreditPurchases] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (admin) {
      loadData()
    }
  }, [admin])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [usersData, merchantsData, businessesData, adsData, purchasesData] = await Promise.all([
        userService.list(),
        merchantService.list(),
        businessService.list(),
        advertisementService.list(),
        creditPurchaseService.list()
      ])
      
      setUsers(usersData)
      setMerchants(merchantsData)
      setBusinesses(businessesData)
      setAdvertisements(adsData)
      setCreditPurchases(purchasesData)
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      setError('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  if (!admin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">Unable to load admin data</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  const activeUsers = users.filter(u => !u.isDeactivated).length
  const pendingApprovals = advertisements.filter((ad: any) => ad.approvalStatus === 'PENDING').length
  const totalRevenue = creditPurchases
    .filter((tp: any) => tp.status === 'COMPLETED')
    .reduce((sum: number, tp: any) => sum + tp.amount, 0)

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-gray-600">Overview of platform metrics</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Users</p>
                <p className="text-2xl font-bold">{formatters.number(users.length)}</p>
                <p className="text-sm text-green-600">{activeUsers} active</p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Merchants</p>
                <p className="text-2xl font-bold">{formatters.number(merchants.length)}</p>
                <p className="text-sm text-gray-500">{businesses.length} businesses</p>
              </div>
              <Building className="w-8 h-8 text-purple-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Advertisements</p>
                <p className="text-2xl font-bold">{formatters.number(advertisements.length)}</p>
                <p className="text-sm text-orange-600">{pendingApprovals} pending</p>
              </div>
              <Megaphone className="w-8 h-8 text-orange-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Revenue</p>
                <p className="text-2xl font-bold">{formatters.currency(totalRevenue)}</p>
                <p className="text-sm text-green-600 flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  Growing
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {advertisements.slice(0, 5).map((ad: any) => (
                <div key={ad.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="text-sm font-medium">{ad.adTitle}</p>
                    <p className="text-xs text-gray-500">{formatters.date(ad.createdAt)}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    ad.approvalStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                    ad.approvalStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {ad.approvalStatus}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Platform Health</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Active Users Rate</span>
                <span className="text-sm font-semibold">
                  {users.length > 0 ? ((activeUsers / users.length) * 100).toFixed(1) : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Pending Approvals</span>
                <span className="text-sm font-semibold">{pendingApprovals}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Businesses</span>
                <span className="text-sm font-semibold">{businesses.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Revenue This Month</span>
                <span className="text-sm font-semibold">{formatters.currency(totalRevenue)}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </AdminLayout>
  )
}